'use client';

import { useCallback, useEffect, useState } from 'react';
import type { ExternalNetworkEntityKind } from '@terra/shared-contracts-external-network';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Button,
  FilterBar,
  IconButton,
  SegmentGroupHorizontal,
  Spinner,
  Switch,
  Text,
  Tooltip,
} from '@terra/ui-design-system';
import {
  AlertCircleIcon,
  Radar01Icon,
  Undo02Icon,
  UnfoldMoreIcon,
} from '@terra/ui-icons';
import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ExternalNetworkGraphFlow,
  ExternalNetworkAssetSearchCombobox,
  useExternalNetworkClusterControls,
  type ExternalNetworkGraphLayout,
} from '@terra/ui-graphs/external-network';
import { useTranslations } from 'next-intl';
import { useTargetId } from '../../store/projectStore';
import { useMainContentTabs } from '../../store/layoutStore';
import { EXPLORE_NEIGHBORHOOD_MAX_DEPTH } from '../../utils/external-network-graph.utils';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  useExternalNetworkExploreGraph,
  type ExternalNetworkGraphSelectedNode,
} from '../../hooks/api/useExternalNetworkExploreGraph';
import { useExternalNetworkNodeExpansion } from '../../hooks/api/useExternalNetworkNodeExpansion';
import { useExternalNetworkGraphVantagePoint } from '../../hooks/api/useExternalNetworkGraphVantagePoint';
import { buildExternalNetworkAssetTab } from './surface-assets/ExternalNetworkAssetsList';
import { EmptyState } from '../EmptyState';
import type { ExternalNetworkAssetEntitySummaryDto } from '@terra/shared-contracts-external-network';

export function ExternalNetworkGraphTab() {
  const surfaceId = useTargetId();
  const t = useTranslations('externalNetwork');
  const tCommon = useTranslations('common');
  const tControls = useTranslations('externalNetwork.graph.controls');
  const { navigateInActiveTab } = useMainContentTabs();
  const {
    graph,
    isLoading,
    isReplacingGraph,
    isExpandingNode,
    isError,
    neighborhoodRoot,
    neighborhoodDepth,
    expandedClusterIds,
    setClusterExpanded,
    isNeighborhoodActive,
    hasActiveExploreView,
    startNeighborhood,
    expandMore,
    expandSelectedNode,
    resetToSurfaceGraph,
  } = useExternalNetworkExploreGraph({ surfaceId });
  const vantagePoint = useExternalNetworkGraphVantagePoint(surfaceId);
  const [focusedAssetId, setFocusedAssetId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [neighborhoodCandidate, setNeighborhoodCandidate] =
    useState<ExternalNetworkGraphSelectedNode | null>(null);
  const [layoutOverride, setLayoutOverride] =
    useState<ExternalNetworkGraphLayout | null>(null);
  const layout = layoutOverride ?? 'standard';

  useEffect(() => {
    if (!graph || graph.nodes.length === 0) {
      setFocusedAssetId(null);

      return;
    }

    if (
      focusedAssetId &&
      !graph.nodes.some((node) => node.id === focusedAssetId)
    ) {
      setFocusedAssetId(null);
    }
  }, [graph, focusedAssetId]);
  const clusterControls = useExternalNetworkClusterControls({
    graph,
    expandedClusterIds,
    isNeighborhoodActive,
  });
  const selectedNodeExpansion = useExternalNetworkNodeExpansion({
    surfaceId,
    graph,
    nodeId: selectedNodeId,
  });
  const clearFocusedSearch = useCallback(() => {
    setFocusedAssetId(null);
  }, []);
  const handleSearchEntitySelect = useCallback(
    (entity: ExternalNetworkAssetEntitySummaryDto) => {
      const isLoaded = graph?.nodes.some((node) => node.id === entity.nodeId);

      setFocusedAssetId(entity.nodeId);
      if (!isLoaded) {
        const selectedNode = {
          id: entity.nodeId,
          kind: entity.entityKind,
        };

        setNeighborhoodCandidate(selectedNode);
        startNeighborhood(selectedNode);

        return;
      }

      if (isNeighborhoodActive) {
        resetToSurfaceGraph();
      }

      setNeighborhoodCandidate(null);
    },
    [
      graph?.nodes,
      isNeighborhoodActive,
      resetToSurfaceGraph,
      startNeighborhood,
    ],
  );
  const searchSurfaceEntities = useCallback(
    ({
      surfaceId: searchSurfaceId,
      term,
      limit,
    }: {
      surfaceId: string;
      term: string;
      limit?: number;
    }) =>
      terraProxyApiClient.searchExternalNetworkEntities({
        surfaceId: searchSurfaceId,
        term,
        limit,
      }),
    [],
  );
  const handleSelectAsset = useCallback(
    (assetId: string, entityKind: ExternalNetworkEntityKind) => {
      const clusterNode = graph?.nodes.find(
        (node) => node.id === assetId && node.nodeType === 'cluster',
      );

      if (clusterNode?.cluster) {
        setSelectedNodeId(null);
        setClusterExpanded({
          clusterId: clusterNode.cluster.id,
          expanded: true,
        });

        return;
      }

      setSelectedNodeId((current) => (current === assetId ? null : assetId));
      setNeighborhoodCandidate({ id: assetId, kind: entityKind });
    },
    [setClusterExpanded, graph?.nodes],
  );
  const handleViewDetails = useCallback(
    (nodeId: string) => {
      const assetNode = graph?.nodes.find((node) => node.id === nodeId);

      if (!assetNode || assetNode.nodeType === 'cluster') {
        return;
      }

      setSelectedNodeId(null);

      if (assetNode.kind === 'WEAKNESS') {
        navigateInActiveTab({
          id: `signal-${assetNode.id}`,
          title: assetNode.label || assetNode.id,
          type: 'signal',
          data: { signalId: assetNode.id },
        });

        return;
      }

      navigateInActiveTab(
        buildExternalNetworkAssetTab({
          entityKind: assetNode.kind,
          entityId: assetNode.id,
          label: assetNode.label ?? assetNode.id,
        }),
      );
    },
    [graph?.nodes, navigateInActiveTab],
  );
  const handleClearSelection = useCallback(() => {
    setSelectedNodeId(null);
  }, []);
  const handleStartNeighborhood = useCallback(() => {
    if (neighborhoodCandidate && !isNeighborhoodActive) {
      clearFocusedSearch();
      startNeighborhood(neighborhoodCandidate);
      setFocusedAssetId(neighborhoodCandidate.id);

      return;
    }

    if (!focusedAssetId || !graph) {
      return;
    }

    const focusedNode = graph.nodes.find((node) => node.id === focusedAssetId);

    if (
      !focusedNode ||
      focusedNode.nodeType === 'cluster' ||
      focusedNode.kind === 'WEAKNESS'
    ) {
      return;
    }

    clearFocusedSearch();
    startNeighborhood({ id: focusedNode.id, kind: focusedNode.kind });
    setFocusedAssetId(focusedNode.id);
  }, [
    clearFocusedSearch,
    focusedAssetId,
    graph,
    isNeighborhoodActive,
    neighborhoodCandidate,
    startNeighborhood,
  ]);
  const filterRightContent =
    clusterControls.length > 0 ||
    focusedAssetId ||
    neighborhoodCandidate ||
    isNeighborhoodActive ? (
      <HStack gap={2} alignItems="center" flexWrap="wrap">
        {clusterControls.map((clusterControl) => (
          <HStack key={clusterControl.id} gap={2} alignItems="center">
            <Switch
              size="sm"
              checked={expandedClusterIds.includes(clusterControl.id)}
              onCheckedChange={(details) =>
                setClusterExpanded({
                  clusterId: clusterControl.id,
                  expanded: details.checked,
                })
              }
            />
            <Text fontSize="sm" color="fg.muted">
              {clusterControl.label}
            </Text>
          </HStack>
        ))}
        {(neighborhoodCandidate || focusedAssetId) && !isNeighborhoodActive ? (
          <Button size="sm" variant="ghost" onClick={handleStartNeighborhood}>
            {t('assetDrawer.expandMore')}
          </Button>
        ) : null}
        {isNeighborhoodActive ? (
          <>
            <Tooltip title={t('assetDrawer.expandMore')}>
              <IconButton
                size="sm"
                variant="ghost"
                loading={isReplacingGraph}
                disabled={neighborhoodDepth >= EXPLORE_NEIGHBORHOOD_MAX_DEPTH}
                aria-label={t('assetDrawer.expandMore')}
                onClick={() => {
                  if (neighborhoodRoot) {
                    setFocusedAssetId(neighborhoodRoot.id);
                  }

                  void expandMore();
                }}
              >
                <UnfoldMoreIcon size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('assetDrawer.viewFullGraph')}>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label={t('assetDrawer.viewFullGraph')}
                onClick={() => {
                  clearFocusedSearch();
                  setNeighborhoodCandidate(null);
                  resetToSurfaceGraph();
                }}
              >
                <Undo02Icon size={16} />
              </IconButton>
            </Tooltip>
          </>
        ) : hasActiveExploreView ? (
          <Tooltip title={t('assetDrawer.viewFullGraph')}>
            <IconButton
              size="sm"
              variant="ghost"
              aria-label={t('assetDrawer.viewFullGraph')}
              onClick={() => {
                clearFocusedSearch();
                setNeighborhoodCandidate(null);
                resetToSurfaceGraph();
              }}
            >
              <Undo02Icon size={16} />
            </IconButton>
          </Tooltip>
        ) : null}
        {focusedAssetId ? (
          <Button size="sm" variant="ghost" onClick={clearFocusedSearch}>
            {t('graph.clearFocus')}
          </Button>
        ) : null}
      </HStack>
    ) : undefined;

  if (!surfaceId) {
    return (
      <EmptyState
        icon={Radar01Icon}
        title={t('graph.noTarget')}
        description={t('graph.noTargetDescription')}
      />
    );
  }

  const graphNodes = graph?.nodes ?? [];

  return (
    <VStack gap={0} alignItems="stretch" h="full" w="full" minH="500px">
      <FilterBar
        filters={[]}
        value={[]}
        onFilterChange={() => undefined}
        leadingCustomFilters={
          surfaceId ? (
            <Box w="280px" minW="180px" maxW="280px" flexShrink={0}>
              <ExternalNetworkAssetSearchCombobox
                surfaceId={surfaceId}
                placeholder={t('graph.searchPlaceholder')}
                onSelect={handleSearchEntitySelect}
                searchEntities={searchSurfaceEntities}
              />
            </Box>
          ) : undefined
        }
        rightContent={
          <HStack gap={2} alignItems="center">
            <SegmentGroupHorizontal
              options={[
                { value: 'sequential', label: tControls('layoutSequential') },
                { value: 'standard', label: tControls('layoutRadial') },
              ]}
              value={layout}
              onValueChange={({ value }) => {
                if (value === 'sequential' || value === 'standard') {
                  setLayoutOverride(value);
                }
              }}
            />
            {filterRightContent}
          </HStack>
        }
        showBottomBorder={true}
        px={4}
        pb={3}
        pt={3}
      />

      {graph?.truncated && graphNodes.length > 0 ? (
        <Box px={4} py={2}>
          <Text fontSize="xs" color="fg.muted">
            {t('graph.truncatedMessage', {
              shownCount: graphNodes.length,
              totalCount: graph.totalCount,
            })}
          </Text>
        </Box>
      ) : null}

      {isError && graphNodes.length > 0 ? (
        <Box px={4} py={2}>
          <Text fontSize="xs" color="fg.error">
            {tCommon('error')}
          </Text>
        </Box>
      ) : null}

      <Box flex={1} minH={0} position="relative">
        {isError && graphNodes.length === 0 ? (
          <EmptyState
            icon={AlertCircleIcon}
            title={t('graph.loadError')}
            description={t('graph.loadErrorDescription')}
          />
        ) : isLoading && graphNodes.length === 0 ? (
          <EmptyState
            icon={Radar01Icon}
            title={t('graph.loadingGraph')}
            isLoading
          />
        ) : !graph || graphNodes.length === 0 ? (
          <EmptyState
            icon={Radar01Icon}
            title={t('graph.noAssetsYet')}
            description={t('graph.noAssetsYetDescription')}
          />
        ) : (
          <ReactFlowProvider>
            <ExternalNetworkGraphFlow
              graph={graph}
              layout={layout}
              showMiniMap={graphNodes.length <= 500}
              onlyRenderVisibleElements={graphNodes.length > 150}
              onSelectAsset={handleSelectAsset}
              onExpandNode={(nodeId) => {
                void expandSelectedNode(nodeId).then((expanded) => {
                  if (expanded) {
                    setFocusedAssetId(nodeId);
                  }
                });
              }}
              onViewDetails={handleViewDetails}
              onClearSelection={handleClearSelection}
              selectedNodeId={selectedNodeId}
              selectedNodeExpansion={selectedNodeExpansion}
              isExpanding={isExpandingNode}
              focusedAssetId={focusedAssetId}
              vantagePoint={vantagePoint}
            />
          </ReactFlowProvider>
        )}
        {isReplacingGraph && graphNodes.length > 0 ? (
          <Box
            position="absolute"
            left={0}
            right={0}
            top={0}
            bottom={0}
            zIndex={10}
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
            bg="bg.default/60"
          >
            <Spinner size="lg" />
          </Box>
        ) : null}
        {isExpandingNode && !isReplacingGraph ? (
          <HStack
            position="absolute"
            left={4}
            bottom={4}
            zIndex={10}
            gap={2}
            alignItems="center"
            bg="bg.default"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="md"
            px={3}
            py={2}
            pointerEvents="none"
          >
            <Spinner size="sm" />
            <Text fontSize="xs" color="fg.muted">
              {t('graph.expandingNode')}
            </Text>
          </HStack>
        ) : null}
      </Box>
    </VStack>
  );
}
